import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { resources, SUPPORTED_LANGUAGES, loadLocaleResources } from './i18n-resources'

const initOptions: any = {
  // Don't hardcode language - let detector work
  fallbackLng: 'en',
  debug: false, // Disable debug to avoid ns_setupCallback error

  interpolation: {
    escapeValue: false,
  },

  detection: {
    order: ['localStorage', 'navigator', 'htmlTag'],
    caches: ['localStorage'],
  },

  ns: ['common', 'admin', 'pages', 'errors', 'components'],
  defaultNS: 'common',

  supportedLngs: SUPPORTED_LANGUAGES,

  react: {
    useSuspense: false,
  },

  // Force immediate loading
  initAsync: true,

  // Preload languages
  preload: SUPPORTED_LANGUAGES,

  // Load missing keys
  saveMissing: false,
  missingKeyHandler: () => {
    // Missing translation tracked
    // In production, this would be sent to a logging service
  },
}

// Static path: only English (the fallback language) is bundled in the entry
// chunk; other locales are code-split and registered on demand. This keeps
// translation growth out of the main-bundle size budget (the 800 kB
// brotli gate failed once all five locales were statically bundled).
const registeredLocales = new Set<string>()

const registerLocale = (lng: string): void => {
  const pending = loadLocaleResources(lng)
  if (!pending) return
  void pending.then((namespaces) => {
    if (registeredLocales.has(lng)) return

    for (const [ns, bundle] of Object.entries(namespaces)) {
      if (!i18n.hasResourceBundle(lng, ns)) {
        i18n.addResourceBundle(lng, ns, bundle, true, false)
      }
    }
    registeredLocales.add(lng)

    // Re-render consumers if the just-loaded locale is the active one.
    if (i18n.language === lng) {
      void i18n.changeLanguage(lng)
    }
  })
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    ...initOptions,
    // Preloading bundled resources only; lazy locales register below.
    preload: ['en'],
    partialBundledLanguages: true,
    resources,
  })
  .then(() => {
    // Load the detected language first so non-English users see their
    // locale as soon as its chunk arrives (fallbackLng renders meanwhile).
    registerLocale(i18n.language)
    // Register future switches from the language picker.
    i18n.on('languageChanged', registerLocale)
    // Prefetch the remaining locales at idle so switching is instant.
    setTimeout(() => {
      for (const lng of SUPPORTED_LANGUAGES) {
        if (lng !== 'en') registerLocale(lng)
      }
    }, 3000)
  })

export default i18n
